import { path } from 'ramda';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ActionsTarget } from '../../constants/Bi';
import { sentryClient } from '../../services/sentryClient';
import { getVideoSessionFromTags } from '../../utils/getVideoSessionFromTags';
import { useTrackActionsClick } from '../../utils/hooks/bi/useTrackActionsClick';
import { useBoolean } from '../../utils/hooks/useBoolean';
import {
  useDownloadAction,
  useVideoMakerActions,
} from '../../utils/hooks/useMyVideoItemActions';
import { VideoPreview } from '../common/VideoPreview';
import {
  CardGalleryItem,
  PopoverMenu,
  TextButton,
  WixIcons,
} from '../common/wixCommon';
import styles from './GalleryItem.scss';
import { IOuter } from './GalleryItem.types';

export const GalleryItem: FC<IOuter> = ({
  file,
  mediaToken,
  onOpenDeleteFileModal,
}) => {
  const { id, thumbnailUrl, name: fileName, createdAt } = file;
  const videoPreviewUrl = path<string>(['videoPreviewUrls', '0'], file);

  const { formatMessage, formatDate, formatTime } = useIntl();

  const { trackActionsClick } = useTrackActionsClick();

  const creationDate = new Date(createdAt * 1000);

  const subtitleDateCreation = formatDate(creationDate, {
    day: 'numeric',
    year: 'numeric',
    month: 'short',
  });

  const subtitleTimeCreation = formatTime(creationDate);

  const {
    editVideoAction,
    shareVideoAction,
    duplicateVideoAction,
  } = useVideoMakerActions({
    file,
  });
  const {
    downloadFileAction,
    isLoading: isDownloadFileActionLoading,
  } = useDownloadAction({
    file,
    mediaToken,
  });

  const [sessionId, updateItemSessionId] = useState('');

  useEffect(() => {
    try {
      updateItemSessionId(getVideoSessionFromTags(file.tags));
    } catch (error) {
      sentryClient.captureException(error);
    }
  }, [file]);

  const handleEditVideoClick = useCallback(() => {
    trackActionsClick({
      target: ActionsTarget.Edit,
      videoId: id,
      sessionId,
    });
    editVideoAction();
  }, [editVideoAction, id, trackActionsClick, sessionId]);

  const handleShareVideoClick = useCallback(() => {
    trackActionsClick({
      target: ActionsTarget.Share,
      videoId: id,
      sessionId,
    });
    shareVideoAction();
  }, [shareVideoAction, id, trackActionsClick, sessionId]);

  const handleDuplicateVideoClick = useCallback(() => {
    trackActionsClick({
      target: ActionsTarget.Duplicate,
      videoId: id,
      sessionId,
    });
    duplicateVideoAction();
  }, [duplicateVideoAction, id, trackActionsClick, sessionId]);

  const handleActionsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      trackActionsClick({
        target: ActionsTarget.ActionsClick,
        videoId: id,
        sessionId,
      });
    },
    [id, trackActionsClick, sessionId],
  );

  const handleActionDownloadClick = useCallback(() => {
    trackActionsClick({
      target: ActionsTarget.Download,
      videoId: id,
      sessionId,
    });
    downloadFileAction();
  }, [downloadFileAction, id, trackActionsClick, sessionId]);

  const handleActionDeleteClick = useCallback(() => {
    trackActionsClick({
      target: ActionsTarget.Delete,
      videoId: file.id,
      sessionId,
    });

    onOpenDeleteFileModal(file);
  }, [file, trackActionsClick, sessionId, onOpenDeleteFileModal]);

  const {
    value: isPlaying,
    setTrue: handleItemNodeMouseEnter,
    setFalse: handleItemNodeMouseLeave,
  } = useBoolean();

  return (
    <div
      className={styles.item}
      onMouseEnter={handleItemNodeMouseEnter}
      onMouseLeave={handleItemNodeMouseLeave}
      data-file-id={id}
    >
      <CardGalleryItem
        dataHook="my-video-item-card"
        title={fileName}
        subtitle={`${subtitleDateCreation} (${subtitleTimeCreation})`}
        backgroundImageNode={
          <VideoPreview
            thumbnailUrl={thumbnailUrl}
            videoPreviewUrl={videoPreviewUrl}
            isPlaying={isPlaying}
          />
        }
        primaryActionProps={{
          label: formatMessage({
            id: 'video-maker-home.main.card.edit.button',
          }),
          onClick: handleEditVideoClick,
        }}
        settingsMenu={
          <PopoverMenu
            dataHook="settings-menu-popover-menu"
            className={styles.popoverHolder}
            appendTo="parent"
            placement="bottom"
            showArrow
            minWidth={186}
            triggerElement={({
              toggle,
              close,
            }: {
              close: (event: React.MouseEvent<HTMLButtonElement>) => void;
              toggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
            }) => (
              <TextButton
                skin="light"
                suffixIcon={<WixIcons.ChevronDown />}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  handleActionsClick(event);
                  toggle(event);
                }}
                onMouseLeave={close}
                dataHook="settings-menu-actions-button"
              >
                <FormattedMessage id="video-maker-home.main.card.actions.button" />
              </TextButton>
            )}
          >
            <PopoverMenu.MenuItem
              prefixIcon={<WixIcons.Share />}
              text={formatMessage({
                id: 'video-maker-home.main.card.tooltip.action.share',
              })}
              onClick={handleShareVideoClick}
              dataHook="settings-menu-action-button-share"
            />

            <PopoverMenu.MenuItem
              prefixIcon={<WixIcons.DownloadImport />}
              text={formatMessage({
                id: 'video-maker-home.main.card.tooltip.action.download',
              })}
              onClick={handleActionDownloadClick}
              dataHook="settings-menu-action-button-download"
              disabled={isDownloadFileActionLoading}
            />

            <PopoverMenu.MenuItem
              prefixIcon={<WixIcons.Duplicate />}
              text={formatMessage({
                id: 'video-maker-home.main.card.tooltip.action.duplicate',
              })}
              onClick={handleDuplicateVideoClick}
              dataHook="settings-menu-action-button-duplicate"
            />

            <PopoverMenu.MenuItem
              prefixIcon={<WixIcons.Delete />}
              text={formatMessage({
                id: 'video-maker-home.main.card.tooltip.action.delete',
              })}
              onClick={handleActionDeleteClick}
              dataHook="settings-menu-action-button-delete"
            />
          </PopoverMenu>
        }
      />
    </div>
  );
};
